import Tesseract from 'tesseract.js';

/**
 * OCR utility — extract text from image, then parse structured data.
 * Supports: Aadhaar, PAN, Driving License, Insurance Card, Lab Reports, Invoices.
 */

/**
 * Extract raw text from image file or data URL.
 * @param {File|string} imageSource — File object or data URL
 * @param {string} lang — 'eng' | 'hin' | 'eng+hin'
 * @returns {string} extracted text
 */
export async function extractText(imageSource, lang = 'eng+hin') {
  const result = await Tesseract.recognize(imageSource, lang, {
    logger: m => {
      if (m.status === 'recognizing text') {
        // Progress available at m.progress (0-1)
      }
    },
  });
  return result.data.text;
}

/**
 * Parse Aadhaar card text into structured data.
 */
function parseAadhaar(text) {
  const data = {};
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  // Aadhaar number: 12 digits (with or without spaces)
  const aadhaarMatch = text.match(/\b(\d{4}\s?\d{4}\s?\d{4})\b/);
  if (aadhaarMatch) data.aadhaar = aadhaarMatch[1].replace(/\s/g, '');

  // DOB patterns
  const dobMatch = text.match(/(?:DOB|Date of Birth|जन्म तिथि|Birth)\s*[:\-]?\s*(\d{1,2}[\s\/\-\.]\d{1,2}[\s\/\-\.]\d{2,4})/i);
  if (dobMatch) {
    const parts = dobMatch[1].split(/[\/\-\.\s]+/);
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2].length === 2 ? '19' + parts[2] : parts[2];
      data.dob = `${year}-${month}-${day}`;
      data.age = Math.floor((new Date() - new Date(data.dob)) / (365.25 * 24 * 3600 * 1000));
    }
  }

  // Gender
  if (/\b(male|पुरुष|MALE)\b/i.test(text)) data.gender = 'Male';
  else if (/\b(female|महिला|FEMALE|स्त्री)\b/i.test(text)) data.gender = 'Female';

  // Name — usually the first prominent line after "Government of India"
  const namePatterns = [
    /(?:Name|नाम)\s*[:\-]?\s*([A-Za-z\s]+)/i,
    /(?:Government of India|भारत सरकार)[\s\S]*?\n\s*([A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/,
  ];
  for (const pat of namePatterns) {
    const m = text.match(pat);
    if (m && m[1].trim().length > 3) { data.name = m[1].trim(); break; }
  }

  // Father's name / S/O / D/O
  const fatherMatch = text.match(/(?:S\/O|D\/O|C\/O|Father|पिता|W\/O)\s*[:\-]?\s*([A-Za-z\s]+)/i);
  if (fatherMatch) data.fatherName = fatherMatch[1].trim();

  // Address
  const addrMatch = text.match(/(?:Address|पता|S\/O[\s\S]*?\n)([\s\S]{20,150}?)(?:\d{6}|\n\n|$)/i);
  if (addrMatch) data.address = addrMatch[1].replace(/\n/g, ', ').replace(/\s+/g, ' ').trim();

  // PIN code
  const pinMatch = text.match(/\b(\d{6})\b/);
  if (pinMatch && pinMatch[1] !== data.aadhaar?.slice(0,6)) data.pincode = pinMatch[1];

  return data;
}

/**
 * Parse PAN card text.
 */
function parsePAN(text) {
  const data = {};
  const panMatch = text.match(/\b([A-Z]{5}\d{4}[A-Z])\b/);
  if (panMatch) data.panNumber = panMatch[1];

  const nameMatch = text.match(/(?:Name|नाम)\s*[:\-]?\s*([A-Z\s]+)/i);
  if (nameMatch) data.name = nameMatch[1].trim();

  const fatherMatch = text.match(/(?:Father|पिता)\s*(?:'s)?\s*(?:Name)?\s*[:\-]?\s*([A-Z\s]+)/i);
  if (fatherMatch) data.fatherName = fatherMatch[1].trim();

  const dobMatch = text.match(/(\d{1,2}[\s\/\-\.]\d{1,2}[\s\/\-\.]\d{2,4})/);
  if (dobMatch) {
    const parts = dobMatch[1].split(/[\/\-\.\s]+/);
    if (parts.length === 3) {
      data.dob = `${parts[2].length === 2 ? '19' + parts[2] : parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
    }
  }

  return data;
}

/**
 * Parse Driving License text.
 */
function parseDrivingLicense(text) {
  const data = {};

  const dlMatch = text.match(/\b([A-Z]{2}\d{2}\s?\d{4}\s?\d{7})\b/);
  if (dlMatch) data.dlNumber = dlMatch[1].replace(/\s/g, '');

  const nameMatch = text.match(/(?:Name|नाम)\s*[:\-]?\s*([A-Za-z\s]+)/i);
  if (nameMatch) data.name = nameMatch[1].trim();

  const dobMatch = text.match(/(?:DOB|Date of Birth)\s*[:\-]?\s*(\d{1,2}[\s\/\-\.]\d{1,2}[\s\/\-\.]\d{2,4})/i);
  if (dobMatch) {
    const parts = dobMatch[1].split(/[\/\-\.\s]+/);
    if (parts.length === 3) {
      data.dob = `${parts[2].length === 2 ? '19' + parts[2] : parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
    }
  }

  const addrMatch = text.match(/(?:Address|पता)\s*[:\-]?\s*([\s\S]{10,150}?)(?:\d{6}|\n\n|$)/i);
  if (addrMatch) data.address = addrMatch[1].replace(/\n/g, ', ').trim();

  if (/\b(male|M)\b/i.test(text)) data.gender = 'Male';
  else if (/\b(female|F)\b/i.test(text)) data.gender = 'Female';

  return data;
}

/**
 * Parse Insurance card text.
 */
function parseInsuranceCard(text) {
  const data = {};

  // Policy number
  const policyMatch = text.match(/(?:Policy|पॉलिसी|Member|ID)\s*(?:No|Number|#)?\s*[:\-]?\s*([A-Z0-9\-\/]+)/i);
  if (policyMatch) data.policyNumber = policyMatch[1];

  // Company name — look for known insurance companies
  const companies = ['Star Health','ICICI Lombard','HDFC ERGO','Bajaj Allianz','New India','United India','National Insurance','Oriental Insurance','TATA AIG','Max Bupa','Niva Bupa','Care Health','Aditya Birla','Manipal Cigna','Religare','SBI General','Cholamandalam'];
  for (const co of companies) {
    if (text.toLowerCase().includes(co.toLowerCase())) { data.companyName = co; break; }
  }

  // Member name
  const nameMatch = text.match(/(?:Name|Member|Insured)\s*[:\-]?\s*([A-Za-z\s]+)/i);
  if (nameMatch) data.memberName = nameMatch[1].trim();

  // Validity
  const validMatch = text.match(/(?:Valid|Expiry|Till|Upto)\s*[:\-]?\s*(\d{1,2}[\s\/\-\.]\d{1,2}[\s\/\-\.]\d{2,4})/i);
  if (validMatch) data.validTill = validMatch[1];

  return data;
}

/**
 * Parse lab report text — extract test values.
 */
function parseLabReport(text) {
  const data = { tests: [] };

  const testPatterns = [
    { regex: /(?:Hemoglobin|Hb|HB)\s*[:\-]?\s*([\d\.]+)\s*(?:g\/dL|gm)/i, name: 'Hemoglobin', unit: 'g/dL' },
    { regex: /(?:WBC|TLC|Total.*Leukocyte)\s*[:\-]?\s*([\d\.]+)/i, name: 'WBC/TLC', unit: '/cumm' },
    { regex: /(?:Platelet)\s*[:\-]?\s*([\d\.]+)/i, name: 'Platelet Count', unit: 'Lakh/cumm' },
    { regex: /(?:RBS|Random.*Blood.*Sugar|Glucose.*Random)\s*[:\-]?\s*([\d\.]+)/i, name: 'Random Blood Sugar', unit: 'mg/dL' },
    { regex: /(?:FBS|Fasting.*Blood.*Sugar|Glucose.*Fasting)\s*[:\-]?\s*([\d\.]+)/i, name: 'Fasting Blood Sugar', unit: 'mg/dL' },
    { regex: /(?:HbA1c|Glycated)\s*[:\-]?\s*([\d\.]+)/i, name: 'HbA1c', unit: '%' },
    { regex: /(?:Creatinine|Serum Creat)\s*[:\-]?\s*([\d\.]+)/i, name: 'Creatinine', unit: 'mg/dL' },
    { regex: /(?:BT|Bleeding.*Time)\s*[:\-]?\s*([\d\.']+)/i, name: 'Bleeding Time', unit: 'min' },
    { regex: /(?:CT|Clotting.*Time)\s*[:\-]?\s*([\d\.']+)/i, name: 'Clotting Time', unit: 'min' },
    { regex: /(?:INR)\s*[:\-]?\s*([\d\.]+)/i, name: 'INR', unit: '' },
    { regex: /(?:PT|Prothrombin)\s*[:\-]?\s*([\d\.]+)/i, name: 'PT', unit: 'sec' },
    { regex: /(?:SGOT|AST)\s*[:\-]?\s*([\d\.]+)/i, name: 'SGOT/AST', unit: 'U/L' },
    { regex: /(?:SGPT|ALT)\s*[:\-]?\s*([\d\.]+)/i, name: 'SGPT/ALT', unit: 'U/L' },
    { regex: /(?:HIV)\s*[:\-]?\s*(Positive|Negative|Reactive|Non)/i, name: 'HIV', unit: '' },
    { regex: /(?:HBsAg|Hepatitis\s*B)\s*[:\-]?\s*(Positive|Negative|Reactive|Non)/i, name: 'HBsAg', unit: '' },
    { regex: /(?:HCV|Hepatitis\s*C)\s*[:\-]?\s*(Positive|Negative|Reactive|Non)/i, name: 'HCV', unit: '' },
  ];

  testPatterns.forEach(tp => {
    const m = text.match(tp.regex);
    if (m) data.tests.push({ name: tp.name, value: m[1], unit: tp.unit });
  });

  // Patient name from report
  const nameMatch = text.match(/(?:Patient|Name)\s*[:\-]?\s*([A-Za-z\s]+)/i);
  if (nameMatch) data.patientName = nameMatch[1].trim();

  return data;
}

/**
 * Parse invoice/receipt text — extract vendor, amount, items.
 */
function parseInvoice(text) {
  const data = { items: [] };

  // Total amount
  const totalMatch = text.match(/(?:Total|Grand Total|Net|Amount|कुल)\s*[:\-]?\s*₹?\s*([\d,]+\.?\d*)/i);
  if (totalMatch) data.totalAmount = parseFloat(totalMatch[1].replace(/,/g, ''));

  // Invoice number
  const invMatch = text.match(/(?:Invoice|Bill|Receipt)\s*(?:No|Number|#)?\s*[:\-]?\s*([A-Z0-9\-\/]+)/i);
  if (invMatch) data.invoiceNo = invMatch[1];

  // Date
  const dateMatch = text.match(/(?:Date|दिनांक)\s*[:\-]?\s*(\d{1,2}[\s\/\-\.]\d{1,2}[\s\/\-\.]\d{2,4})/i);
  if (dateMatch) data.date = dateMatch[1];

  // GSTIN
  const gstMatch = text.match(/\b(\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z\d]{2})\b/);
  if (gstMatch) data.gstin = gstMatch[1];

  return data;
}

/**
 * Main OCR function — detect document type and parse accordingly.
 * @param {File|string} imageSource
 * @param {string} docType — 'aadhaar' | 'pan' | 'dl' | 'insurance' | 'lab' | 'invoice' | 'auto'
 * @returns {{ rawText, parsed, docType }}
 */
export async function scanDocument(imageSource, docType = 'auto') {
  const rawText = await extractText(imageSource, 'eng+hin');

  // Auto-detect document type if not specified
  if (docType === 'auto') {
    if (/aadhaar|आधार|uidai|uid/i.test(rawText)) docType = 'aadhaar';
    else if (/permanent.*account|income.*tax|pan/i.test(rawText)) docType = 'pan';
    else if (/driving|licence|license|transport|motor/i.test(rawText)) docType = 'dl';
    else if (/insurance|policy|health.*card|bima|member.*id/i.test(rawText)) docType = 'insurance';
    else if (/hemoglobin|platelet|blood.*sugar|glucose|CBC|test.*report|pathology|laboratory/i.test(rawText)) docType = 'lab';
    else if (/invoice|bill|receipt|total.*amount|grand.*total|GST/i.test(rawText)) docType = 'invoice';
    else docType = 'unknown';
  }

  let parsed = {};
  switch (docType) {
    case 'aadhaar': parsed = parseAadhaar(rawText); break;
    case 'pan': parsed = parsePAN(rawText); break;
    case 'dl': parsed = parseDrivingLicense(rawText); break;
    case 'insurance': parsed = parseInsuranceCard(rawText); break;
    case 'lab': parsed = parseLabReport(rawText); break;
    case 'invoice': parsed = parseInvoice(rawText); break;
    default: parsed = {};
  }

  return { rawText, parsed, docType };
}
